import React from 'react';
import Navbar from '../Components/Navbar';
import HeroSection from '../Components/HeroSection';
import Footer from '../Components/Footer';
import {
  BuildingOffice2Icon,
  EnvelopeIcon,
  MapPinIcon,
  PhoneIcon,
} from '@heroicons/react/24/solid';

const quickContacts = [
  {
    title: 'WhatsApp',
    value: '+1 763-227-8620',
    note: 'Best for quick questions and follow-up',
    href: 'https://wa.me/17632278620',
    external: true,
    iconBg: 'bg-emerald-50',
    icon: (
      <img
        src="/images/whatsapp.png"
        alt="WhatsApp"
        className="h-5 w-5"
      />
    ),
  },
  {
    title: 'Email for Contact',
    value: 'birimo.insurance@gmail.com',
    note: 'General support and coverage questions',
    href: 'mailto:birimo.insurance@gmail.com',
    iconBg: 'bg-blue-50',
    icon: <EnvelopeIcon className="h-5 w-5 text-navcolor" />,
  },
  {
    title: 'Email for Documents',
    value: 'info@birimoinsurance.com',
    note: 'Send paperwork and requested documents',
    href: 'mailto:info@birimoinsurance.com',
    iconBg: 'bg-violet-50',
    featured: true,
    icon: <EnvelopeIcon className="h-5 w-5 text-navcolor" />,
  },
  {
    title: 'Office Location',
    value: 'Minneapolis, Minnesota',
    note: 'Tap to open directions',
    href: 'https://maps.app.goo.gl/W7qP1NitSHK6xWPr9?g_st=iw',
    external: true,
    iconBg: 'bg-slate-100',
    icon: <MapPinIcon className="h-5 w-5 text-navcolor" />,
  },
];

const teamMembers = [
  {
    name: 'Abshir Mohamed',
    role: 'Property & Casualty Specialist',
    image: '/images/abshir.jpg',
    email: 'abshir@birimoinsurance.com',
    phone: '651-315-2321',
    phoneHref: 'tel:+16513152321',
    whatsapp: '651-315-2321',
    whatsappHref: 'https://wa.me/16513152321',
    highlights: [
      'Property & Casualty',
      'English, Arabic, Somali',
      'Helping and educating clients',
    ],
    paragraphs: [
      'Abshir Mohamed brings many years of property and casualty insurance experience to Birimo Insurance, helping clients find protection that fits their real needs.',
      'He speaks English, Arabic, and Somali, making it easier for families across our community to feel understood, supported, and informed.',
      'His approach is centered on helping and educating clients so they can make confident decisions about their coverage.',
    ],
  },
  {
    name: 'Faysal Hared',
    role: 'Insurance Career Development',
    image: '/images/faysal.jpeg',
    email: 'FHared@birimoinsurance.com',
    phone: '763-227-8620',
    phoneHref: 'tel:+17632278620',
    whatsappHref: 'https://wa.me/17632278620',
    highlights: [
      'Insurance Field',
      'Technology Skills',
      'Learning and Growth',
    ],
    paragraphs: [
      'I am a motivated and curious individual who is always eager to learn and grow. My goal is to build a strong career in the insurance field, and I am passionate about gaining new knowledge and developing practical skills every day.',
      'I have a strong interest in technology, especially computer skills and editing. I enjoy working with digital tools, learning new software, and improving my abilities step by step.',
      'I am responsible, hardworking, and ready to take on new challenges. I look forward to opportunities where I can learn, contribute, and grow into a skilled professional.',
    ],
  },
];

const ContactUs = () => {
  return (
    <>
      <Navbar />
      <HeroSection />

      <div className="min-h-screen bg-slate-50">
        <section className="relative overflow-hidden bg-navcolor">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute -left-12 top-10 h-40 w-40 rounded-full bg-cyan-300 blur-3xl" />
            <div className="absolute right-0 top-0 h-56 w-56 rounded-full bg-blue-300 blur-3xl" />
            <div className="absolute bottom-0 left-1/3 h-44 w-44 rounded-full bg-teal-300 blur-3xl" />
          </div>

          <div className="relative mx-auto flex max-w-6xl flex-col gap-6 px-6 py-16 md:px-8">
            <p className="text-sm font-semibold uppercase tracking-[0.35em] text-blue-100">
              Contact Us
            </p>
            <div className="max-w-3xl">
              <h1 className="font-serif text-4xl font-bold leading-tight text-white md:text-6xl">
                Meet the team behind Birimo Insurance Agency
              </h1>
              <p className="mt-5 max-w-2xl text-base leading-7 text-blue-50 md:text-lg">
                Local guidance, multilingual support, and real client education
                for families across Minneapolis and beyond.
              </p>
            </div>
          </div>
        </section>

        <section className="border-y border-slate-200 bg-[#e7edf6]">
          <div className="mx-auto max-w-6xl px-6 py-10 md:px-8">
            <div className="text-center">
              <p className="text-xs font-semibold uppercase tracking-[0.32em] text-slate-500">
                Minneapolis Office
              </p>
              <h2 className="mt-3 font-serif text-3xl font-bold text-navcolor md:text-4xl">
                Minneapolis Client Support Team
              </h2>
              <p className="mt-3 text-sm text-slate-700 md:text-base">
                Personal service rooted in trust, clarity, and community.
              </p>
            </div>

            <div className="mt-10 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
              {quickContacts.map((contact) => (
                <a
                  key={contact.title}
                  href={contact.href}
                  target={contact.external ? '_blank' : undefined}
                  rel={contact.external ? 'noopener noreferrer' : undefined}
                  className={`group flex min-h-[188px] flex-col rounded-[28px] border bg-white p-6 shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-lg ${contact.featured
                    ? 'border-navcolor/35 shadow-[0_12px_28px_rgba(66,35,255,0.12)]'
                    : 'border-slate-200'
                    }`}
                >
                  <div
                    className={`flex h-12 w-12 items-center justify-center rounded-2xl ${contact.iconBg}`}
                  >
                    {contact.icon}
                  </div>

                  <p className="mt-5 text-[11px] font-bold uppercase tracking-[0.3em] leading-5 text-slate-500">
                    {contact.title}
                  </p>
                  <p className="mt-3 text-[15px] font-semibold leading-6 text-slate-900 transition group-hover:text-navcolor [overflow-wrap:anywhere] md:text-base">
                    {contact.value}
                  </p>
                  <p className="mt-auto pt-4 text-sm leading-5 text-slate-500">
                    {contact.note}
                  </p>
                </a>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-[#e5e5e5] px-6 py-16 md:px-8">
          <div className="mx-auto max-w-6xl text-center">
            <div className="mb-10 flex flex-col items-center gap-3">
              <BuildingOffice2Icon className="h-9 w-9 text-navcolor" />
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.28em] text-slate-500">
                  Our Team
                </p>
                <h3 className="mt-2 font-serif text-4xl font-bold text-navcolor md:text-5xl">
                  Meet Our Team
                </h3>
              </div>
              <p className="max-w-2xl text-base leading-7 text-slate-600">
                Dedicated support for every coverage conversation, with local
                guidance and personal service from our Minneapolis team.
              </p>
            </div>
          </div>

          <div className="mx-auto grid max-w-6xl gap-6 md:grid-cols-2">
            {teamMembers.map((member) => (
              <article
                key={member.name}
                className="flex h-full flex-col items-center rounded-[22px] border border-white bg-white px-6 py-8 text-center shadow-[0_16px_34px_rgba(15,23,42,0.16)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_20px_44px_rgba(15,23,42,0.2)]"
              >
                <div className="rounded-full border-[5px] border-navcolor/20 bg-[#f7f7f7] p-2">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="h-36 w-36 rounded-full object-cover md:h-40 md:w-40"
                    style={{ objectPosition: member.imagePosition || 'center' }}
                  />
                </div>

                <h4 className="mt-7 font-serif text-2xl font-bold text-slate-900">
                  {member.name}
                </h4>
                <p className="mt-2 text-sm font-bold uppercase tracking-[0.16em] text-slate-900">
                  {member.role}
                </p>

                <div className="mt-5 flex flex-wrap justify-center gap-2">
                  {member.highlights.map((highlight) => (
                    <span
                      key={highlight}
                      className="rounded-full border border-navcolor/10 bg-blue-50 px-3 py-1 text-xs font-semibold text-slate-700"
                    >
                      {highlight}
                    </span>
                  ))}
                </div>

                <div className="mt-6 flex flex-1 flex-col justify-start space-y-3 text-sm italic leading-6 text-slate-600">
                  {member.paragraphs.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}
                </div>

                <div className="mt-7 flex flex-col gap-3 text-sm font-semibold text-slate-700">
                  <a
                    href={`mailto:${member.email}`}
                    className="flex items-center justify-center gap-2 transition hover:text-navcolor [overflow-wrap:anywhere]"
                  >
                    <EnvelopeIcon className="h-5 w-5 shrink-0 text-navcolor" />
                    <span>{member.email}</span>
                  </a>

                  {member.phone && (
                    <a
                      href={member.phoneHref}
                      className="flex items-center justify-center gap-2 transition hover:text-navcolor"
                    >
                      <PhoneIcon className="h-5 w-5 text-navcolor" />
                      <span>{member.phone}</span>
                    </a>
                  )}

                  {member.whatsapp && (
                    <a
                      href={member.whatsappHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 transition hover:text-navcolor"
                    >
                      <img
                        src="/images/whatsapp.png"
                        alt="WhatsApp"
                        className="h-5 w-5"
                      />
                      <span>WhatsApp</span>
                    </a>
                  )}
                </div>
              </article>
            ))}
          </div>

          <div className="mx-auto mt-10 max-w-6xl rounded-[30px] bg-navcolor px-6 py-8 text-white shadow-xl md:px-10">
            <div className="grid gap-6 md:grid-cols-[1.4fr_0.6fr] md:items-center">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.3em] text-blue-100">
                  Birimo Insurance Agency
                </p>
                <h4 className="mt-3 font-serif text-3xl font-bold">
                  Reach out and let's find the right coverage together
                </h4>
                <p className="mt-4 max-w-3xl text-blue-50">
                  Whether you need help with property and casualty coverage or
                  life insurance guidance, our team is here to serve you with
                  patience, education, and local support.
                </p>
              </div>

              <div className="flex flex-col gap-3">
                <a
                  href="mailto:info@birimoinsurance.com"
                  className="rounded-full bg-white px-5 py-3 text-center font-semibold text-navcolor transition hover:bg-blue-50"
                >
                  Email Our Team
                </a>
                <a
                  href="https://wa.me/17632278620"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-full border border-blue-200 px-5 py-3 text-center font-semibold text-white transition hover:bg-white/10"
                >
                  Message on WhatsApp
                </a>
                <a
                  href="https://maps.app.goo.gl/W7qP1NitSHK6xWPr9?g_st=iw"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 text-sm text-blue-100 underline-offset-4 transition hover:underline"
                >
                  <MapPinIcon className="h-4 w-4" />
                  Minneapolis, Minnesota
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </>
  );
};

export default ContactUs;
